import React, { useMemo, useState } from 'react';
import { PlayIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { Waveform } from '../../components/archive/AudioTile';
import { Button } from '../../components/ui/Button';
import { ChoiceChip } from '../../components/ui/ChoiceChip';
import { FilterMenu } from '../../components/ui/FilterMenu';
import { TextInput } from '../../components/ui/TextControl';
import { ArchiveTile } from '../../components/archive/ArchiveTile';
import { ArchiveDetail } from '../../components/archive/ArchiveDetail';
import { ARCHIVE_ITEMS, ARCHIVE_KIND_LABELS, decadeOf, itemsInYears, thinYears } from '../../data/archive';
import type { ArchiveItem, ArchiveKind } from '../../data/archive';
import { CURRENT_MEMBER, DECADES, parseYears } from '../../data/community';
const KINDS: ArchiveKind[] = ['photograph', 'audio', 'memory', 'artifact', 'record'];
export function Archive() {
  const [query, setQuery] = useState('');
  const [decade, setDecade] = useState<string | null>(null);
  const [kind, setKind] = useState<ArchiveKind | null>(null);
  const [myYearsOnly, setMyYearsOnly] = useState(false);
  const [open, setOpen] = useState<ArchiveItem | null>(null);
  const you = parseYears(CURRENT_MEMBER.years);
  const yours = itemsInYears(you.start, you.end);
  const thin = thinYears(you.start, you.end);
  const results = useMemo(() => ARCHIVE_ITEMS.filter(item => {
    if (decade && decadeOf(item.year) !== decade) return false;
    if (kind && item.kind !== kind) return false;
    if (myYearsOnly && (item.year < you.start || item.year > you.end)) return false;
    if (query) {
      const haystack = `${item.title} ${item.quote ?? ''} ${item.credit}`.toLowerCase();
      if (!haystack.includes(query.toLowerCase())) return false;
    }
    return true;
  }).sort((a, b) => a.year - b.year), [decade, kind, myYearsOnly, query, you.start, you.end]);
  return <div className="min-h-full bg-card" data-id="element-567" data-mp="31lqynrs1xz1o">
      {/* YOUR YEARS — the archive indexed by who you are, before any filter. */}
      <section aria-labelledby="your-years" className="bg-canvas" data-id="element-568" data-mp="38rtg686spa02">
        <div className="mx-auto w-full max-w-6xl px-4 pb-6 pt-6 sm:px-6" data-id="element-569" data-mp="3dniiuke4370l">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-570" data-mp="3indc2uld08ot">
            <h1 id="your-years" className="text-section-heading font-semibold text-ink" data-id="element-571" data-mp="3nmpyje8bjdqv">
              {CURRENT_MEMBER.years}: the years you were here
            </h1>
            <p className="text-small text-ink-muted" data-id="element-572" data-mp="2px4qstvj1860">
              {yours.length} {yours.length === 1 ? 'item' : 'items'} in the archive
            </p>
          </div>

          <ul className="mt-4 flex snap-x gap-3 overflow-x-auto pb-1" data-id="element-573" data-mp="2zwn8f3nyshuw">
            {yours.map(item => <li key={item.id} className="w-56 shrink-0 snap-start overflow-hidden rounded-field border border-rule bg-card" data-id="element-574" data-mp="34poqtlndqzj8">
                <button type="button" onClick={() => setOpen(item)} className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset" data-id="element-575" data-mp="2c6sv6kzqgra4">
                  {item.kind === 'audio' ? <span className="flex h-28 flex-col justify-center gap-2 bg-ink px-4" data-id="element-576" data-mp="kagox0s9d71c">
                      <span className="flex items-center gap-2 text-highlight" data-id="element-577" data-mp="fc5h0vlix6lr">
                        <PlayIcon aria-hidden="true" className="h-4 w-4 shrink-0" data-id="element-578" data-mp="ae412jje9nei" />
                        <Waveform seed={item.id} className="h-6 min-w-0 flex-1" data-id="element-579" data-mp="5mlxrotwfaqd" />
                      </span>
                      <span className="text-small text-on-ink opacity-70" data-id="element-580" data-mp="1kqyepvj37f8">
                        Recording · {item.duration}
                      </span>
                    </span> : item.image ? <img src={item.image.src} alt={item.image.alt} className="h-28 w-full object-cover" data-id="element-581" data-mp="3nrffltq7ftos" /> : <span className="flex h-28 items-center px-4 text-body italic text-ink-muted" data-id="element-582" data-mp="1mratezbp3x2y">
                      {item.quote ? `“${item.quote.slice(0, 60)}…”` : item.title}
                    </span>}
                  <span className="flex flex-col gap-0.5 p-3" data-id="element-583" data-mp="1cyv8bt53qsdu">
                    <span className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-584" data-mp="1hy7z2oz3gjlp">
                      {item.year} · {ARCHIVE_KIND_LABELS[item.kind]}
                    </span>
                    <span className="line-clamp-2 text-small text-ink" data-id="element-585" data-mp="190p8l52old2f">{item.title}</span>
                  </span>
                </button>
              </li>)}
          </ul>

          {thin.length > 0 ? <div className="mt-4 flex flex-col gap-3 rounded-field border border-rule bg-card p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-586" data-mp="z16numey5n9b">
              <p className="text-small text-ink-muted" data-id="element-587" data-mp="2tfhcouvjkhf3">
                <span className="font-medium text-ink" data-id="element-588" data-mp="2tcv6ozoau9yz">
                  {thin.map(entry => entry.year).join(', ')}
                </span>{' '}
                {thin.length === 1 ? 'has' : 'have'} almost nothing in{' '}
                {thin.length === 1 ? 'it' : 'them'}. You were here, so whatever you add
                won&apos;t be one account among many.
              </p>
              <Button className="shrink-0" data-id="element-589" data-mp="mp49c4728e7n">
                <PlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-590" data-mp="10hu0so5cybc5" />
                Add something
              </Button>
            </div> : null}
        </div>
      </section>

      {/* EVERYTHING ELSE */}
      <div className="border-b border-rule bg-canvas" data-id="element-591" data-mp="1agt6pyyqnsvg">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 pb-6 sm:px-6" data-id="element-592" data-mp="1ae7144vernfv">
          <div className="flex flex-wrap items-baseline gap-x-3" data-id="element-593" data-mp="15hzsg4bmnxu8">
            <h2 className="text-heading font-semibold text-ink" data-id="element-594" data-mp="3c8pi98kwp2q8">Fifty years</h2>
            <p className="text-small text-ink-muted" data-id="element-595" data-mp="37934my161kgw">
              {results.length} of {ARCHIVE_ITEMS.length} shown
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-id="element-596" data-mp="1b79jbouz4o15">
            <label className="relative flex min-w-44 max-w-xs flex-1 items-center" data-id="element-597" data-mp="1g5jg23wcjwov">
              <SearchIcon aria-hidden="true" className="pointer-events-none absolute left-3.5 h-4 w-4 text-ink-muted" data-id="element-598" data-mp="1k5liwgdtiw5q" />
              <span className="sr-only" data-id="element-599" data-mp="1p2tshskwhrvd">Search the archive</span>
              <TextInput type="search" placeholder="Search the archive" className="pl-10" value={query} onChange={event => setQuery(event.target.value)} data-id="element-600" data-mp="1u3hpcuvt92xi" />
            </label>

            <ChoiceChip checked={myYearsOnly} onChange={() => setMyYearsOnly(!myYearsOnly)} className="shrink-0" data-id="element-601" data-mp="27vxohkllcgt5">
              My years
            </ChoiceChip>

            <ChoiceChip checked={kind === 'memory'} onChange={() => setKind(kind === 'memory' ? null : 'memory')} className="shrink-0" data-id="element-602" data-mp="2hun7xht4y9bi">
              Memories
            </ChoiceChip>

            <span aria-hidden="true" className="h-6 w-px shrink-0 bg-rule" data-id="element-603" data-mp="2n123xo1d7n2s" />

            <div className="shrink-0" data-id="element-604" data-mp="2hzwa1mvyxtro">
            <FilterMenu label="Years" allLabel="All" value={decade} onChange={setDecade} options={[{
              label: 'All years',
              value: null
            }, ...DECADES.map(item => ({
              label: item,
              value: item
            }))]} data-id="element-605" data-mp="2dy11bxbpke3v" />
            </div>

            <div className="shrink-0" data-id="element-606" data-mp="30x5riyhqgx6a">
            <FilterMenu label="Kind" allLabel="Everything" value={kind} onChange={next => setKind(next as ArchiveKind | null)} options={[{
              label: 'Everything',
              value: null
            }, ...KINDS.map(item => ({
              label: ARCHIVE_KIND_LABELS[item],
              value: item
            }))]} data-id="element-607" data-mp="30w47i4iqp0cf" />
            </div>

            <Button variant="secondary" className="ml-auto shrink-0" data-id="element-608" data-mp="gxflazzkj5e7">
              <PlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-609" data-mp="gyqr15ckqn7y" />
              Add to the archive
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6" data-id="element-610" data-mp="76tdvt209l6l">
        {results.length > 0 ? <ul className="columns-1 gap-4 sm:columns-2 lg:columns-3 [&>li]:mb-4" data-id="element-611" data-mp="uy8hrq9fkajy">
            {results.map(item => <ArchiveTile key={item.id} item={item} onOpen={setOpen} data-id="element-612" data-mp="zvrvmp27ulbd" />)}
          </ul> : <div className="rounded-card border border-dashed border-rule p-10 text-center" data-id="element-613" data-mp="kyrflite0esq">
            <p className="text-body text-ink" data-id="element-614" data-mp="pw9cuby1l5kw">Nothing here matches that.</p>
            <p className="mx-auto mt-2 max-w-sm text-small text-ink-muted" data-id="element-615" data-mp="35o6xtpejiv6r">
              Thin years are the norm rather than the exception. Most of this archive is what
              people brought back.
            </p>
          </div>}

        <p className="mt-8 border-t border-rule pt-6 text-small text-ink-muted" data-id="element-616" data-mp="1ssj9oyzenc6y">
          That&apos;s the whole archive: {ARCHIVE_ITEMS.length} items so far, and it only grows
          when someone adds to it.
        </p>
      </div>

      <ArchiveDetail item={open} onClose={() => setOpen(null)} data-id="element-617" data-mp="1xpje75glw6vy" />
    </div>;
}